import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { useAuthStore } from './store/useAuthStore';
import { AppLayout } from './components/AppLayout';
import { Auth } from './components/Auth';
import { ROUTES } from './routes';
import { Loader2 } from 'lucide-react';

// ─── Lazy-loaded pages ──────────────────────────────────────────────────────

const Dashboard = lazy(() => import('./pages/Dashboard'));
const VisualBrain = lazy(() => import('./pages/VisualBrain'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const NotesPage = lazy(() => import('./pages/NotesPage'));
const GoalsPage = lazy(() => import('./pages/GoalsPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const HabitsPage = lazy(() => import('./pages/HabitsPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const SynapsePage = lazy(() => import('./pages/SynapsePage'));
const RoadmapPage = lazy(() => import('./pages/RoadmapPage'));
const MentionsLegalesPage = lazy(() => import('./pages/MentionsLegalesPage'));
const CGUPage = lazy(() => import('./pages/CGUPage'));
const CGVPage = lazy(() => import('./pages/CGVPage'));
const ConfidentialitePage = lazy(() => import('./pages/ConfidentialitePage'));
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const KanbanPage = lazy(() => import('./pages/KanbanPage'));
const GanttPage = lazy(() => import('./pages/GanttPage'));
const FinancesPage = lazy(() => import('./pages/FinancesPage'));
const TimelinePage = lazy(() => import('./pages/TimelinePage'));

// Non-lazy pages (small, always loaded)
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { AbonnementPage } from './pages/AbonnementPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { GatedView } from './components/GatedView';
import { canAccessView, VIEW_ACCESS } from './lib/capabilities';
import { useUserTier } from './hooks/useUserTier';
import { Wallet, Columns, GanttChart } from 'lucide-react';

// ─── Gated route components ─────────────────────────────────────────────────

function GatedRouteFinances() {
  const tier = useUserTier();
  return canAccessView('finances', tier) ? (
    <FinancesPage />
  ) : (
    <GatedView
      viewName={VIEW_ACCESS.finances.viewName}
      description={VIEW_ACCESS.finances.description}
      requiredPlan="Pro"
      comingSoon={VIEW_ACCESS.finances.comingSoon}
      icon={Wallet}
    />
  );
}

function GatedRouteKanban() {
  const tier = useUserTier();
  return canAccessView('kanban', tier) ? (
    <KanbanPage />
  ) : (
    <GatedView
      viewName={VIEW_ACCESS.kanban.viewName}
      description={VIEW_ACCESS.kanban.description}
      requiredPlan="Pro"
      comingSoon={VIEW_ACCESS.kanban.comingSoon}
      icon={Columns}
    />
  );
}

function GatedRouteGantt() {
  const tier = useUserTier();
  return canAccessView('gantt', tier) ? (
    <GanttPage />
  ) : (
    <GatedView
      viewName={VIEW_ACCESS.gantt.viewName}
      description={VIEW_ACCESS.gantt.description}
      requiredPlan="Max"
      comingSoon={VIEW_ACCESS.gantt.comingSoon}
      icon={GanttChart}
    />
  );
}

// ─── Route guards ───────────────────────────────────────────────────────────

function PublicRoute({ children }: { children: React.ReactNode }) {
  const session = useAuthStore((s) => s.session);
  if (session) return <Navigate to={ROUTES.dashboard} replace />;
  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const session = useAuthStore((s) => s.session);
  if (!session) return <Navigate to={ROUTES.login} replace />;
  return <>{children}</>;
}

// ─── Suspense fallback ──────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="h-full w-full bg-zinc-950 flex items-center justify-center">
      <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
    </div>
  );
}

// ─── App routes ─────────────────────────────────────────────────────────────

export function AppRoutes() {
  const isPasswordRecovery = useAuthStore((s) => s.isPasswordRecovery);
  const clearPasswordRecovery = useAuthStore((s) => s.clearPasswordRecovery);

  // VB-024F — Password recovery mode: show recovery form regardless of other routes
  if (isPasswordRecovery) {
    return <Auth initialMode="password-recovery" onPasswordUpdateDone={clearPasswordRecovery} />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path={ROUTES.landing} element={<LandingPage />} />
      <Route
        path={ROUTES.mentionsLegales}
        element={
          <Suspense fallback={<PageLoader />}>
            <MentionsLegalesPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.cgu}
        element={
          <Suspense fallback={<PageLoader />}>
            <CGUPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.cgv}
        element={
          <Suspense fallback={<PageLoader />}>
            <CGVPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.confidentialite}
        element={
          <Suspense fallback={<PageLoader />}>
            <ConfidentialitePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.login}
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected routes — layout with Sidebar */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route
          path={ROUTES.dashboard}
          element={
            <Suspense fallback={<PageLoader />}>
              <Dashboard />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.synapse}
          element={
            <Suspense fallback={<PageLoader />}>
              <SynapsePage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.brain}
          element={
            <Suspense fallback={<PageLoader />}>
              <VisualBrain />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.tasks}
          element={
            <Suspense fallback={<PageLoader />}>
              <TasksPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.calendar}
          element={
            <Suspense fallback={<PageLoader />}>
              <CalendarPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.events}
          element={
            <Suspense fallback={<PageLoader />}>
              <EventsPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.notes}
          element={
            <Suspense fallback={<PageLoader />}>
              <NotesPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.goals}
          element={
            <Suspense fallback={<PageLoader />}>
              <GoalsPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.contacts}
          element={
            <Suspense fallback={<PageLoader />}>
              <ContactsPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.habits}
          element={
            <Suspense fallback={<PageLoader />}>
              <HabitsPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.domains}
          element={
            <Suspense fallback={<PageLoader />}>
              <div className="flex items-center justify-center h-full text-zinc-500">
                Module en construction...
              </div>
            </Suspense>
          }
        />
        <Route
          path={ROUTES.months}
          element={
            <Suspense fallback={<PageLoader />}>
              <div className="flex items-center justify-center h-full text-zinc-500">
                Module en construction...
              </div>
            </Suspense>
          }
        />
        <Route
          path={ROUTES.finances}
          element={
            <Suspense fallback={<PageLoader />}>
              <GatedRouteFinances />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.kanban}
          element={
            <Suspense fallback={<PageLoader />}>
              <GatedRouteKanban />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.gantt}
          element={
            <Suspense fallback={<PageLoader />}>
              <GatedRouteGantt />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.roadmap}
          element={
            <Suspense fallback={<PageLoader />}>
              <RoadmapPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.timeline}
          element={
            <Suspense fallback={<PageLoader />}>
              <TimelinePage />
            </Suspense>
          }
        />
        <Route path={ROUTES.feedback} element={<FeedbackPage />} />
        <Route
          path={ROUTES.parametres}
          element={
            <Suspense fallback={<PageLoader />}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route path={ROUTES.subscription} element={<AbonnementPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
